import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { m, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import { Container } from './Container';
import { Button } from './Button';
import { useAnalytics } from '../../hooks/useAnalytics';
import { onPricingClick } from '../../utils/compoundEvents';
import { fireGoogleAdsConversion } from '../../utils/googleAdsConversions';

interface HeaderProps {
  logo?: string;
}

const HeaderWrapper = styled.header<{ $scrolled: boolean; $isDark: boolean }>`
  position: sticky;
  top: 0;
  z-index: ${props => props.theme.zIndex.sticky};
  background: ${props =>
    props.$scrolled
      ? props.$isDark
        ? props.theme.colors.dark.background + 'f0'
        : props.theme.colors.light.background + 'f0'
      : 'transparent'};
  backdrop-filter: ${props => (props.$scrolled ? 'blur(10px)' : 'none')};
  border-bottom: 1px solid
    ${props =>
      props.$scrolled
        ? props.$isDark
          ? props.theme.colors.dark.border
          : props.theme.colors.light.border
        : 'transparent'};
  transition: all ${props => props.theme.transitions.base};
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 80px;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    height: 64px;
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  transition: transform ${props => props.theme.transitions.fast};
  outline: none;

  &:hover {
    transform: scale(1.05);
    text-decoration: none;
  }

  &:focus {
    outline: none;
  }

  img {
    height: 36px;
    width: auto;
  }
`;

const LogoImage = styled.img<{ $isDark: boolean }>`
  height: 36px;
  width: auto;
  aspect-ratio: 170 / 36;
  max-width: 170px;
  filter: ${props =>
    props.$isDark
      ? 'none'
      : 'brightness(0) saturate(100%) invert(27%) sepia(81%) saturate(3426%) hue-rotate(211deg) brightness(99%) contrast(98%)'};
`;

const LogoWrapper = styled.div`
  position: relative;
  height: 36px;
  width: 170px;
  flex-shrink: 0;
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[8]};

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: none;
  }
`;

const NavLinks = styled.ul`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[6]};
`;

const NavLink = styled(Link)<{ $isDark: boolean }>`
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => (props.$isDark ? props.theme.colors.dark.text : props.theme.colors.light.text)};
  transition: color ${props => props.theme.transitions.fast};
  position: relative;
  outline: none;

  &:hover {
    color: ${props => props.theme.colors.primary};
    text-decoration: none;
  }

  &:focus {
    outline: none;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 2px;
    background: ${props => props.theme.colors.primary};
    transition: width ${props => props.theme.transitions.fast};
  }

  &:hover::after {
    width: 100%;
  }
`;

const ExternalNavLink = styled.a<{ $isDark: boolean }>`
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => (props.$isDark ? props.theme.colors.dark.text : props.theme.colors.light.text)};
  transition: color ${props => props.theme.transitions.fast};
  position: relative;

  &:hover {
    color: ${props => props.theme.colors.primary};
    text-decoration: none;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 2px;
    background: ${props => props.theme.colors.primary};
    transition: width ${props => props.theme.transitions.fast};
  }

  &:hover::after {
    width: 100%;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[4]};
`;

const ThemeToggle = styled.button<{ $isDark: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: ${props => props.theme.borderRadius.full};
  background: ${props =>
    props.$isDark
      ? props.theme.colors.dark.cardHover
      : props.theme.colors.light.backgroundSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${props => props.theme.transitions.fast};
  color: ${props => (props.$isDark ? props.theme.colors.dark.text : props.theme.colors.light.text)};

  &:hover {
    background: ${props =>
      props.$isDark ? props.theme.colors.dark.border : props.theme.colors.light.border};
    transform: scale(1.1);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const MobileMenuButton = styled.button<{ $isDark: boolean }>`
  display: none;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  color: ${props => (props.$isDark ? props.theme.colors.dark.text : props.theme.colors.light.text)};

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: flex;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const MobileMenuOverlay = styled(m.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9998;
  backdrop-filter: blur(4px);
`;

const MobileMenu = styled(m.div)<{ $isDark: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 85%;
  max-width: 320px;
  background: ${props =>
    props.$isDark
      ? `linear-gradient(135deg, ${props.theme.colors.dark.background} 0%, ${props.theme.colors.dark.backgroundSecondary} 100%)`
      : `linear-gradient(135deg, ${props.theme.colors.light.background} 0%, ${props.theme.colors.light.backgroundSecondary} 100%)`};
  padding: ${props => props.theme.spacing[8]} ${props => props.theme.spacing[6]};
  z-index: 9999;
  box-shadow: -10px 0 30px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

const MobileMenuHeader = styled.div<{ $isDark: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing[8]};
  padding-bottom: ${props => props.theme.spacing[4]};
  border-bottom: 1px solid
    ${props => (props.$isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')};
`;

const MobileCloseButton = styled.button<{ $isDark: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: ${props => props.theme.borderRadius.full};
  background: ${props => (props.$isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)')};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${props => props.theme.transitions.fast};
  color: ${props => (props.$isDark ? props.theme.colors.dark.text : props.theme.colors.light.text)};
  margin-left: auto;

  &:hover {
    background: ${props => (props.$isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')};
    transform: rotate(90deg);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const MobileNavLinks = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[2]};
  flex: 1;
`;

const MobileNavLink = styled(Link)<{ $isDark: boolean }>`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => (props.$isDark ? props.theme.colors.dark.text : props.theme.colors.light.text)};
  padding: ${props => props.theme.spacing[4]} ${props => props.theme.spacing[4]};
  display: block;
  outline: none;
  border-radius: ${props => props.theme.borderRadius.lg};
  transition: all ${props => props.theme.transitions.fast};
  position: relative;

  &:hover {
    background: ${props => (props.$isDark ? 'rgba(10, 93, 235, 0.1)' : 'rgba(10, 93, 235, 0.05)')};
    color: ${props => props.theme.colors.primary};
    transform: translateX(8px);
  }

  &:focus {
    outline: none;
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 0;
    background: ${props => props.theme.colors.primary};
    transition: height ${props => props.theme.transitions.fast};
    border-radius: 3px;
  }

  &:hover::before {
    height: 24px;
  }
`;

const MobileExternalNavLink = styled.a<{ $isDark: boolean }>`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => (props.$isDark ? props.theme.colors.dark.text : props.theme.colors.light.text)};
  padding: ${props => props.theme.spacing[3]} 0;
  display: block;
`;

// Icons as simple SVG components
const SunIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
    />
  </svg>
);

const MoonIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
    />
  </svg>
);

const MenuIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 6h16M4 12h16M4 18h16"
    />
  </svg>
);

const CloseIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export const Header: React.FC<HeaderProps> = ({ logo }) => {
  const { isDark, toggleTheme } = useTheme();
  const { trackEvent } = useAnalytics();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [mobileMenuOpen]);

  const navItems = [
    { label: 'AI-Powered Launch', path: '/ai-powered-launch' },
    { label: 'Features', path: '/features' },
    { label: 'How It Works', path: '/how-it-works' },
    { label: 'Pricing', path: '/pricing' },
    { label: 'Articles', path: '/marketplace-insights' },
  ];

  const formatNavLabel = (label: string) =>
    label
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

  const handleNavClick = (label: string, path?: string) => {
    if (path === '/pricing') {
      onPricingClick();
    }
    trackEvent('navigation', 'click', `header:${formatNavLabel(label)}`);
  };

  const handleLogoClick = () => {
    trackEvent('navigation', 'click', 'header:logo');
  };

  const handleScheduleDemoClick = () => {
    trackEvent('cta', 'click', 'header:schedule_demo');
    fireGoogleAdsConversion('ctaHero', 5);
  };

  return (
    <>
      <HeaderWrapper $scrolled={scrolled} $isDark={isDark}>
        <Container>
          <HeaderContent>
            <Logo to="/" onClick={handleLogoClick}>
              <LogoWrapper>
                <AnimatePresence mode="wait">
                  <m.div
                    key={isDark ? 'dark' : 'light'}
                    initial={{ opacity: 1 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <LogoImage
                      src="/cnxion-logo.svg"
                      alt="CNXION"
                      width={170}
                      height={36}
                      $isDark={isDark}
                    />
                  </m.div>
                </AnimatePresence>
              </LogoWrapper>
            </Logo>

            <Nav>
              <NavLinks>
                {navItems.map(item => (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      $isDark={isDark}
                      onClick={() => handleNavClick(item.label, item.path)}
                    >
                      {item.label}
                    </NavLink>
                  </li>
                ))}
              </NavLinks>
            </Nav>

            <Actions>
              <ThemeToggle onClick={toggleTheme} $isDark={isDark} aria-label="Toggle theme">
                {isDark ? <SunIcon /> : <MoonIcon />}
              </ThemeToggle>

              <Button
                variant="primary"
                size="small"
                as={Link}
                to="/demo"
                onClick={handleScheduleDemoClick}
              >
                Schedule Demo
              </Button>

              <MobileMenuButton
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                $isDark={isDark}
                aria-label="Toggle menu"
              >
                <MenuIcon />
              </MobileMenuButton>
            </Actions>
          </HeaderContent>
        </Container>
      </HeaderWrapper>

      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <MobileMenuOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => setMobileMenuOpen(false)}
            />
            <MobileMenu
              $isDark={isDark}
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{
                type: 'spring',
                damping: 25,
                stiffness: 300,
              }}
            >
              <MobileMenuHeader $isDark={isDark}>
                <Logo
                  to="/"
                  onClick={() => {
                    handleLogoClick();
                    setMobileMenuOpen(false);
                  }}
                >
                  <LogoImage
                    src="/cnxion-logo.svg"
                    alt="CNXION"
                    width={170}
                    height={36}
                    $isDark={isDark}
                  />
                </Logo>
                <MobileCloseButton
                  onClick={() => setMobileMenuOpen(false)}
                  $isDark={isDark}
                  aria-label="Close menu"
                >
                  <CloseIcon />
                </MobileCloseButton>
              </MobileMenuHeader>

              <MobileNavLinks>
                {navItems.map(item => (
                  <li key={item.path}>
                    <MobileNavLink
                      to={item.path}
                      $isDark={isDark}
                      onClick={() => {
                        handleNavClick(item.label, item.path);
                        setMobileMenuOpen(false);
                      }}
                    >
                      {item.label}
                    </MobileNavLink>
                  </li>
                ))}
              </MobileNavLinks>

              <div style={{ marginTop: 'auto' }}>
                <Button
                  variant="primary"
                  fullWidth
                  as={Link}
                  to="/demo"
                  onClick={() => {
                    handleScheduleDemoClick();
                    setMobileMenuOpen(false);
                  }}
                  style={{ marginBottom: '1rem' }}
                >
                  Schedule Demo
                </Button>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: '1rem',
                    borderTop: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                  }}
                >
                  <ThemeToggle
                    onClick={toggleTheme}
                    $isDark={isDark}
                    aria-label="Toggle theme"
                    style={{ width: '48px', height: '48px' }}
                  >
                    {isDark ? <SunIcon /> : <MoonIcon />}
                  </ThemeToggle>
                </div>
              </div>
            </MobileMenu>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
